import React, { useState, useEffect, useRef } from "react";
import { Camera, X, Search, Upload } from "lucide-react";
import jsQR from "jsqr";
import { uiAlert } from "../../../components/alerts/customAlert/uiAlert";

const QRScannerOverlay = ({ openOverlay, closeOverlay, transID, setTransID, showQueriedTrans }) => {
  const [showOverlay, setShowOverlay] = useState(openOverlay);
  const [isMobile, setIsMobile] = useState(false);
  const [transactionId, setTransactionId] = useState("");
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState("");
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const canvasRef = useRef(null);
  const animationFrameRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    setTransID("");

    const checkMobile = () => {
      setIsMobile(/iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => {
      window.removeEventListener("resize", checkMobile);
    };
  }, []);

  useEffect(() => {
    if (scanResult) {
      setTransID(scanResult);
      stopCamera();
      handleQuery(scanResult);
    }
  }, [scanResult]);

  useEffect(() => {
    if (showOverlay && isMobile) {
      startCamera();
    }

    return () => {
      stopCamera();
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [showOverlay, isMobile]);

  /*  useEffect(() => {
  
    let alive = true;
    const load = () =>
      new Promise((res, rej) => {
        if (window.jsQR) return res();
        const s = document.createElement("script");
        s.src = "https://cdnjs.cloudflare.com/ajax/libs/jsqr/1.4.0/jsQR.min.js";
        s.async = true;
        s.onload = res;
        s.onerror = rej;
        document.body.appendChild(s);
      });

    (async () => {
      await load();
      if (!alive) return;
      // now start camera + scan loop exactly as above,
      // but call window.jsQR(...) instead of imported jsQR
    })();

    return () => {
      alive = false; // stop camera + RAF 
    };
  }, []); */

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment", width: { ideal: 1280 }, height: { ideal: 720 } },
      });

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
        setScanning(true);

        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play();
          scanQRCode();
        };
      }
    } catch (err) {
      console.error("Error accessing camera:", err);
      uiAlert("Unable to access camera. Please check permissions.", "error");
    }
  };

  const scanQRCode = () => {
    if (!videoRef.current || !canvasRef.current) return;
    /* if (!window.jsQR) {
      animationFrameRef.current = requestAnimationFrame(scanQRCode);
      return;
    } */

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d", { willReadFrequently: true });

    if (video.readyState === video.HAVE_ENOUGH_DATA) {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
      const code = jsQR(imageData.data, imageData.width, imageData.height, {
        inversionAttempts: "attemptBoth",
      });
      console.log(code, "detected code");

      if (code) {
        // draw outline
        drawOutline(context, code);
        setScanResult(code.data);

        if (navigator.vibrate) {
          navigator.vibrate(200);
        }

        return;
      }
    }

    animationFrameRef.current = requestAnimationFrame(scanQRCode);
  };

  // Draw detected QR outline
  const drawOutline = (ctx, code) => {
    const loc = code.location;
    ctx.beginPath();
    ctx.moveTo(loc.topLeftCorner.x, loc.topLeftCorner.y);
    ctx.lineTo(loc.topRightCorner.x, loc.topRightCorner.y);
    ctx.lineTo(loc.bottomRightCorner.x, loc.bottomRightCorner.y);
    ctx.lineTo(loc.bottomLeftCorner.x, loc.bottomLeftCorner.y);
    ctx.closePath();
    ctx.lineWidth = 4;
    ctx.strokeStyle = "rgba(0,255,0,0.8)";
    ctx.stroke();
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    setScanning(false);
  };

  const handleClose = () => {
    setShowOverlay(false);

    setScanResult("");
    stopCamera();
    closeOverlay(false);
  };

  const handleQuery = () => {
    if (scanResult || transID) {
      showQueriedTrans(true);

      handleClose();
    }
  };

  const startDesktopScan = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 1280 }, height: { ideal: 720 } },
      });

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
        setScanning(true);

        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play();
          scanQRCode();
        };
      }
    } catch (err) {
      console.error("Error accessing camera:", err);
      uiAlert("Unable to access camera. Please check permissions.", "error");
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        if (!window.jsQR) {
          alert("QR scanner is still loading. Please try again in a moment.");
          return;
        }

        const canvas = canvasRef.current;
        const context = canvas.getContext("2d");
        canvas.width = img.width;
        canvas.height = img.height;
        context.drawImage(img, 0, 0);

        const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
        const code = window.jsQR(imageData.data, imageData.width, imageData.height);

        if (code) {
          setScanResult(code.data);
          setTransID(code.data);
          uiAlert("QR code scanned successfully!", "success");
        } else {
          uiAlert("No QR code found in the image. Please try another image.", "error");
        }
      };
      img.src = event.target.result;
    };
    reader.readAsDataURL(file);
  };

  const handleScanClick = () => {
    setShowOverlay(true);
  };

  return (
    <>
      <style>{`
        .qr__scanner__wrapper__8x4k9 {
          padding: 32px;
        }
        
        .qr__header__container__7m2p5 {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 16px;
          background-color: #ffffff;
          border-radius: 8px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          max-width: 448px;
        }
        
        .qr__header__content__3n8w1 {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        
        .qr__header__title__5k9r2 {
          color: #2563eb;
          font-weight: 600;
        }
        
        .qr__badge__count__6h3t8 {
          background-color: #dbeafe;
          color: #2563eb;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 14px;
          font-weight: 500;
        }
        
        .qr__scan__button__4p7j1 {
          margin-left: auto;
          padding: 8px;
          background: none;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          transition: background-color 0.2s;
        }
        
        .qr__scan__button__4p7j1:hover {
          background-color: #f3f4f6;
        }
        
        .qr__overlay__backdrop__9w5m3 {
          position: fixed;
          inset: 0;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 16px;
        }
        
        .qr__modal__container__2t6n8 {
          background-color: #ffffff;
          border-radius: 8px;
          box-shadow: 0 20px 25px rgba(0, 0, 0, 0.15);
          max-width: 448px;
          width: 100%;
          position: relative;
        }
        
        .qr__close__button__1r9k4 {
          position: absolute;
          top: 16px;
          right: 16px;
          padding: 8px;
          background: none;
          border: none;
          border-radius: 9999px;
          cursor: pointer;
          transition: background-color 0.2s;
          z-index: 10;
        }
        
        .qr__close__button__1r9k4:hover {
          background-color: #f3f4f6;
        }
        
        .qr__modal__content__8h2v5 {
          padding: 24px;
        }
        
        .qr__modal__title__3w9p7 {
          font-size: 20px;
          font-weight: 600;
          color: #1f2937;
          margin-bottom: 16px;
        }
        
        .qr__content__section__6k1m9 {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }
        
        .qr__success__box__5n3r8 {
          background-color: #f0fdf4;
          border: 1px solid #bbf7d0;
          border-radius: 8px;
          padding: 16px;
        }
        
        .qr__success__title__7j4w2 {
          font-size: 14px;
          font-weight: 500;
          color: #166534;
          margin-bottom: 8px;
        }
        
        .qr__success__text__9p2k6 {
          font-size: 14px;
          color: #15803d;
          word-break: break-all;
        }
        
        .qr__video__container__4m8t1 {
          position: relative;
          background-color: #111827;
          border-radius: 8px;
          overflow: hidden;
          aspect-ratio: 1;
        }
        
        .qr__video__container__desktop__2h7k9 {
          aspect-ratio: 16/9;
        }
        
        .qr__video__element__8r5n3 {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        
        .qr__canvas__hidden__1t9j5 {
          display: none;
        }
        
        .qr__loading__overlay__6w3p8 {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .qr__loading__content__7m4k2 {
          color: #ffffff;
          text-align: center;
        }
        
        .qr__loading__icon__5h9r1 {
          width: 64px;
          height: 64px;
          margin: 0 auto 8px;
          opacity: 0.5;
        }
        
        .qr__scan__frame__9k2w7 {
          position: absolute;
          inset: 0;
          border: 2px solid #ffffff;
          opacity: 0.5;
          margin: 32px;
          border-radius: 8px;
        }
        
        .qr__scanning__indicator__3p6n5 {
          position: absolute;
          bottom: 16px;
          left: 0;
          right: 0;
          text-align: center;
        }
        
        .qr__scanning__text__8w1m4 {
          color: #ffffff;
          font-size: 14px;
          background-color: rgba(0, 0, 0, 0.5);
          display: inline-block;
          padding: 8px 16px;
          border-radius: 9999px;
        }
        
        .qr__help__text__2k7r9 {
          font-size: 14px;
          color: #4b5563;
          text-align: center;
        }
        
        .qr__form__group__5j3n8 {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        
        .qr__input__label__9w4k1 {
          display: block;
          font-size: 14px;
          font-weight: 500;
          color: #374151;
        }
        
        .qr__text__input__6h8p2 {
          width: 100%;
          padding: 12px 16px;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 14px;
          outline: none;
        }
        
        .qr__text__input__6h8p2:focus {
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }
        
        .qr__button__group__7m2p4 {
          display: flex;
          gap: 12px;
        }
        
        .qr__primary__button__8k3w9 {
          flex: 1;
          background-color: #2563eb;
          color: #ffffff;
          padding: 12px 16px;
          border-radius: 8px;
          border: none;
          cursor: pointer;
          transition: background-color 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          font-size: 14px;
        }
        
        .qr__primary__button__8k3w9:hover {
          background-color: #1d4ed8;
        }
        
        .qr__primary__button__8k3w9:disabled {
          background-color: #d1d5db;
          cursor: not-allowed;
        }
        
        .qr__secondary__button__5n9r2 {
          padding: 8px 16px;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          background-color: transparent;
          cursor: pointer;
          transition: background-color 0.2s;
        }
        
        .qr__secondary__button__5n9r2:hover {
          background-color: #f9fafb;
        }
        
        .qr__full__width__button__3w8k7 {
          width: 100%;
          background-color: #2563eb;
          color: #ffffff;
          padding: 8px 16px;
          border-radius: 8px;
          border: none;
          cursor: pointer;
          transition: background-color 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          font-size: 14px;
        }
        
        .qr__full__width__button__3w8k7:hover {
          background-color: #1d4ed8;
        }
        
        .qr__file__input__hidden__4p7m1 {
          display: none;
        }
        
        .qr__small__help__text__9k5w3 {
          font-size: 12px;
          color: #6b7280;
          text-align: center;
        }
      `}</style>

      <div className='qr__scanner__wrapper__8x4k9'>
        {showOverlay && (
          <div className='qr__overlay__backdrop__9w5m3'>
            <div className='qr__modal__container__2t6n8'>
              <button onClick={handleClose} className='qr__close__button__1r9k4' aria-label='Close'>
                <X className='w-5 h-5 text-gray-600' />
              </button>

              <div className='qr__modal__content__8h2v5'>
                <h2 className='qr__modal__title__3w9p7'>{isMobile ? "Scan QR Code" : "Search Transaction"}</h2>

                {isMobile ? (
                  <div className='qr__content__section__6k1m9'>
                    {scanResult ? (
                      <div className='qr__success__box__5n3r8'>
                        <p className='qr__success__title__7j4w2'>QR Code Scanned Successfully!</p>
                        <p className='qr__success__text__9p2k6'>{scanResult}</p>
                      </div>
                    ) : (
                      <>
                        <div className='qr__video__container__4m8t1'>
                          <video ref={videoRef} autoPlay playsInline muted className='qr__video__element__8r5n3' />
                          <canvas ref={canvasRef} className='qr__canvas__hidden__1t9j5' />
                          {!scanning && (
                            <div className='qr__loading__overlay__6w3p8'>
                              <div className='qr__loading__content__7m4k2'>
                                <Camera className='qr__loading__icon__5h9r1' />
                                <p>Initializing camera...</p>
                              </div>
                            </div>
                          )}
                          <div className='qr__scan__frame__9k2w7'></div>
                          {scanning && (
                            <div className='qr__scanning__indicator__3p6n5'>
                              <p className='qr__scanning__text__8w1m4'>Scanning...</p>
                            </div>
                          )}
                        </div>
                        <p className='qr__help__text__2k7r9'>Position the QR code within the frame</p>
                      </>
                    )}

                    {scanResult && (
                      <button onClick={handleQuery} className='qr__full__width__button__3w8k7'>
                        <Search className='w-4 h-4' />
                        Search Transaction
                      </button>
                    )}
                  </div>
                ) : (
                  <div className='qr__content__section__6k1m9'>
                    {scanning ? (
                      <div className='qr__content__section__6k1m9'>
                        {scanResult ? (
                          <div className='qr__success__box__5n3r8'>
                            <p className='qr__success__title__7j4w2'>QR Code Scanned!</p>
                            <p className='qr__success__text__9p2k6'>{scanResult}</p>
                          </div>
                        ) : (
                          <>
                            <div className='qr__video__container__4m8t1 qr__video__container__desktop__2h7k9'>
                              <video ref={videoRef} autoPlay playsInline muted className='qr__video__element__8r5n3' />
                              <canvas ref={canvasRef} className='qr__canvas__hidden__1t9j5' />
                              <div className='qr__scan__frame__9k2w7'></div>
                              <div className='qr__scanning__indicator__3p6n5'>
                                <p className='qr__scanning__text__8w1m4'>Scanning for QR code...</p>
                              </div>
                            </div>
                            <button
                              onClick={() => {
                                stopCamera();
                              }}
                              className='qr__full__width__button__3w8k7'
                              style={{ backgroundColor: "#fff", color: "#374151", border: "1px solid #d1d5db" }}
                            >
                              Cancel Scan
                            </button>
                          </>
                        )}
                      </div>
                    ) : (
                      <>
                        <div className='qr__form__group__5j3n8'>
                          <label htmlFor='transactionId' className='qr__input__label__9w4k1'>
                            Transaction ID
                          </label>
                          <input
                            id='transactionId'
                            type='text'
                            value={transID}
                            onInput={(e) => setTransID(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && handleQuery()}
                            placeholder='Enter or scan transaction ID'
                            className='qr__text__input__6h8p2'
                          />
                        </div>

                        <div className='qr__button__group__7m2p4'>
                          <button onClick={handleQuery} disabled={!transID.trim()} className='qr__primary__button__8k3w9'>
                            <Search className='w-4 h-4' />
                            Search
                          </button>
                          {/*  <button onClick={startDesktopScan} className='qr__secondary__button__5n9r2' title='Scan with webcam'>
                            <Camera className='w-5 h-5 text-gray-600' />
                          </button> */}
                          {/*  <button onClick={() => fileInputRef.current?.click()} className='qr__secondary__button__5n9r2' title='Upload QR code image'>
                            <Upload className='w-5 h-5 text-gray-600' />
                          </button> */}
                        </div>
                        {/* 
                        <input ref={fileInputRef} type='file' accept='image/*' onChange={handleFileUpload} className='qr__file__input__hidden__4p7m1' /> */}

                        <p className='qr__small__help__text__9k5w3'>Scan with scanner or type in the trans ID</p>
                      </>
                    )}

                    {scanResult && (
                      <button onClick={handleQuery} className='qr__full__width__button__3w8k7'>
                        <Search className='w-4 h-4' />
                        Search Transaction
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default QRScannerOverlay;
